<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from "vue";
import { vIntersectionObserver } from "@vueuse/components";
import VText from "@magnit/core/src/components/VText/VText.vue";
import VIcon from "@magnit/icons/src/components/VIcon/VIcon.vue";
import scrollLock from "@magnit/core/src/helpers/scrollLock";
import VWrapper from "@magnit/core/src/components/VWrapper/VWrapper.vue";
import ArrowIcon from "@magnit/icons/src/assets/icons/arrow-down-corner-small-24.svg";
import VNavigationLink from "./VNavigationLink.vue";
import VNavigationSubmenu from "./VNavigationSubmenu.vue";
import { MenuItems, MENU_LINKS, SUBMENU_LINKS } from "./VNavigation.vars";
import type {
  INavigationElementsNames,
  ISubMenuLink,
  INavigationProps,
  INavigationEmits,
} from "./VNavigation.types";

const props = withDefaults(defineProps<INavigationProps>(), {
  activeItem: MenuItems.MAGNIT,
  moreLabel: "Ещё",
  phone: "",
  links: () => MENU_LINKS,
  submenuLinks: () => SUBMENU_LINKS,
});

const emit = defineEmits<INavigationEmits>();

const isMenuOpen = ref<boolean>(false);

const onElementClick = (name: INavigationElementsNames) => {
  emit("click", name);
};

const onSubmenuToggle = () => {
  onElementClick("more");
  isMenuOpen.value = !isMenuOpen.value;
};
const onVisibilitySubmenuToggle = () => {
  emit("viewSubmenuToggle");
};
const onSubMenuLinkClick = (link: ISubMenuLink) => {
  isMenuOpen.value = false;
  emit("submenuClick", link.url, link.label);
};

const onVisibilityLink = (
  [{ isIntersecting }]: IntersectionObserverEntry[],
  name: INavigationElementsNames,
) => {
  if (!isIntersecting) return;
  emit("viewLink", name);
};

onMounted(() => {
  emit("view");
  const link = document.querySelector(".pl-navigation-link__active");
  if (!link) return;
  link.scrollIntoView({
    behavior: "smooth",
    block: "nearest",
    inline: "center",
  });
});

onUnmounted(() => {
  scrollLock.disable();
});

watch(
  () => isMenuOpen.value,
  (isActive) => {
    if (isActive) {
      scrollLock.enable();
    } else {
      scrollLock.disable();
    }
  },
);
</script>

<template>
  <div class="pl-navigation">
    <VWrapper
      class="pl-navigation__container"
      :indent="{ xs: '12px', s: '20px', m: '32px' }"
    >
      <div class="pl-navigation__links">
        <VNavigationLink
          v-for="link in props.links"
          :key="link.name"
          v-intersection-observer="
            (arg: IntersectionObserverEntry[]) =>
              onVisibilityLink(arg, link.name)
          "
          :active="link.name === props.activeItem"
          v-bind="link"
          @click="onElementClick(link.name)"
        />

        <div
          v-if="props.submenuLinks"
          v-intersection-observer="onVisibilitySubmenuToggle"
          class="pl-navigation__more"
          :class="[
            {
              'pl-navigation__more_active': isMenuOpen,
            },
          ]"
          @click="onSubmenuToggle"
        >
          <div class="pl-navigation__more-container">
            <VText
              class="pl-navigation__more-label"
              :color="isMenuOpen ? 'primary' : 'secondary'"
              font="body-large-accent"
              >{{ props.moreLabel }}</VText
            >

            <VIcon
              class="pl-navigation__more-icon"
              :color="isMenuOpen ? 'primary' : 'secondary'"
            >
              <ArrowIcon />
            </VIcon>
          </div>
        </div>
      </div>

      <VText
        :href="`tel:${props.phone}`"
        class="pl-navigation__phone"
        tag="a"
        color="secondary"
        font="body-small-accent"
        @click="onElementClick('phone')"
      >
        {{ props.phone }}
      </VText>
    </VWrapper>
    <VNavigationSubmenu
      v-if="isMenuOpen && props.submenuLinks"
      :menu="props.submenuLinks"
      @close="onSubmenuToggle"
      @link-click="onSubMenuLinkClick"
    />

    <hr class="pl-navigation__line" />
  </div>
</template>

<style lang="postcss" scoped>
.pl-navigation__line {
  border: 0;
  background-color: var(--pl-divider-primary);
  padding: 0;
  margin: -1px 0 0;
  height: 1px;
  width: 100%;
}

.pl-navigation__container {
  .pl-navigation & {
    -ms-overflow-style: none;
    align-items: center;
    display: flex;
    justify-content: space-between;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
}

.pl-navigation__links {
  align-items: center;
  display: flex;
}

.pl-navigation__phone {
  display: none;
  text-decoration: none;
  white-space: nowrap;
}

.pl-navigation__more-container {
  align-items: center;
  cursor: pointer;
  display: flex;
  height: 55px;
}

.pl-navigation__more-label {
  .pl-navigation & {
    margin-right: 8px;
    line-height: 24px;
    user-select: none;
  }
}

.pl-navigation__more-icon {
  .pl-navigation & {
    transition: transform 0.2s;
  }

  .pl-navigation__more_active & {
    transform: rotate(180deg);
  }
}

@media (--pl-viewport-from-ml) {
  .pl-navigation__phone {
    display: block;
  }
}
</style>
